import { useRef, useCallback, useMemo } from "react";
import { HEADER } from "@/components/Header/constants";
import { View, StyleSheet, ScrollView } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useRouter, type RelativePathString } from "expo-router";
import { Button } from "@/components/ui/Button";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useToolTip } from "@/hooks/useToolTip";
import useContextMenu from "@/hooks/useContextMenu";
import usePermissions from "@/hooks/usePermissions";
import useAuth from "@/hooks/useAuth";
import type { PermissionEntity } from "@/types/permissions";

// Entities with create screens under (root), mapped to MCI icons
const CREATABLE_ENTITIES: { key: PermissionEntity; icon: keyof typeof MaterialCommunityIcons.glyphMap }[] = [
  { key: "projects", icon: "hammer-wrench" },
  { key: "shifts", icon: "calendar-clock" },
  { key: "users", icon: "account" },
  { key: "clients", icon: "domain" },
  { key: "contacts", icon: "card-account-details-outline" },
  { key: "time_cards", icon: "clock-outline" },
  { key: "expenses", icon: "cash" },
  { key: "invoices", icon: "file-document-outline" },
  { key: "estimates", icon: "calculator-variant-outline" },
  { key: "courses", icon: "school-outline" },
  { key: "incidents", icon: "alert-outline" },
  { key: "investigations", icon: "magnify" },
  { key: "applications", icon: "account-plus-outline" },
];

export function QuickActionsButton() {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const router = useRouter();
  const toolTip = useToolTip();
  const contextMenu = useContextMenu();
  const permissions = usePermissions();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const creatableItems = useMemo(
    () => CREATABLE_ENTITIES.filter((e) => permissions[e.key]?.read && permissions[e.key]?.create),
    [permissions],
  );

  const handlePointerEnter = useCallback(() => {
    isHovering.current = true;
    toolTip.setToolTipContent(
      <View
        style={{
          backgroundColor: colors.background,
          paddingHorizontal: 8,
          paddingVertical: 4,
          borderRadius: 8,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border,
        }}
      >
        <ThemedText style={{ fontSize: 12 }}>Quick Actions</ThemedText>
      </View>,
    );
    ref.current?.measureInWindow((x, y, w, h) => {
      if (!isHovering.current) return;
      toolTip.setLocation({ x: x + (w || 0) / 2 - 50, y: y + (h || 0) + 4, width: 100, height: 30 } as any);
      toolTip.setVisible(true);
    });
  }, [colors.background, colors.border, toolTip]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  const handlePress = useCallback(() => {
    toolTip.setVisible(false);
    if (contextMenu.visible) {
      contextMenu.setVisible(false);
      return;
    }
    ref.current?.measureInWindow((x, y, w, h) => {
      const menuWidth = 240;
      contextMenu.setLocation({ x: x + (w || 0) - menuWidth, y: y + (h || 0) + 4, width: menuWidth });
      contextMenu.setIgnoreRef(ref);
      contextMenu.setMenuContent(
        <ScrollView style={{ maxHeight: 380 }}>
          <ThemedText style={{ paddingHorizontal: 8, paddingTop: 4, paddingBottom: 8, opacity: 0.5, fontSize: 12 }}>
            Create New
          </ThemedText>
          {creatableItems.map((item) => {
            const name = (displayNames as any)?.[item.key]?.name || item.key;
            return (
              <Button
                key={item.key}
                text={`New ${name}`}
                icon={item.icon}
                iconStyle={{ color: colors.placeholder }}
                iconPosition="left"
                style={{
                  gap: 8,
                  justifyContent: "flex-start",
                  backgroundColor: "transparent",
                  borderColor: "transparent",
                }}
                onPress={() => {
                  contextMenu.setVisible(false);
                  router.push({
                    pathname: `/${item.key}` as RelativePathString,
                    params: { new: "true", panel: item.key.replace(/s$/, "") },
                  });
                }}
              />
            );
          })}
          {creatableItems.length === 0 && (
            <ThemedText style={{ padding: 8, opacity: 0.5, fontSize: 13 }}>No create permissions</ThemedText>
          )}
        </ScrollView>,
      );
      contextMenu.setVisible(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu, toolTip, creatableItems, displayNames]);

  return (
    <View ref={ref} onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}>
      <Button
        icon="lightning-bolt"
        onPress={handlePress}
        style={{ height: HEADER.ICON_SIZE, width: HEADER.ICON_SIZE }}
        iconSize={HEADER.ICON_PX}
      />
    </View>
  );
}
